/**
 * analytics-shared/hooks/useShares.js
 *
 * React surface for the wedge-3 sharing endpoints.
 *
 *   useAssetShares(assetType, assetId)
 *     Lists current shares for a single report or dashboard. Returns the
 *     raw response (with `total_shares`, `shares_by_type`, `shares`) plus
 *     a `refetch` so the ShareModal can re-sync after a mutation.
 *
 *   useShareMutations(assetType, assetId)
 *     `grant`, `update`, `revoke` mutations scoped to a single asset.
 *     onSuccess callbacks both invalidate the asset's share list AND
 *     write fresh data into the cache so the UI updates without waiting
 *     for the refetch round-trip (the "real-time" optimistic feel
 *     requested in the spec).
 *
 * Mutations bubble errors instead of swallowing them so the modal can
 * show inline failure messages — match the rest of the analytics module.
 */
import {
  useQuery, useMutation, invalidateQueries, getQueryData, setQueryData,
} from './queryShim';
import { sharesApi } from '../api/sharesApi';

const keyFor = (assetType, assetId) => ['shares', assetType, assetId];

export function useAssetShares(assetType, assetId) {
  const q = useQuery({
    queryKey: keyFor(assetType, assetId),
    queryFn: () => sharesApi.listForAsset(assetType, assetId),
    enabled: !!(assetType && assetId),
    staleTime: 5_000,
  });
  return {
    ...q,
    shares: q.data?.shares || [],
    totalShares: q.data?.total_shares ?? 0,
    sharesByType: q.data?.shares_by_type || { user: [], group: [], role: [] },
  };
}

export function useShareMutations(assetType, assetId) {
  const key = keyFor(assetType, assetId);

  // Optimistic cache rewrite — we know the server's response shape
  // matches the `shares_by_type` bucketing, so we can splice the new
  // share into the cached structure for instant feedback. invalidate
  // afterwards so the timestamped/server-resolved fields catch up.
  const overlayShare = (share) => {
    const current = getQueryData(key);
    if (!current) return;
    // Replace any existing row on the same (asset, principal) tuple,
    // then push if not already present.
    const filtered = (current.shares || []).filter((s) => !(
      s.shared_with_type === share.shared_with_type
      && s.shared_with_id === share.shared_with_id
    ));
    const nextShares = [share, ...filtered];
    const byType = { user: [], group: [], role: [] };
    nextShares.forEach((s) => {
      const bucket = byType[s.shared_with_type];
      if (bucket) bucket.push(s);
    });
    setQueryData(key, {
      ...current,
      total_shares: nextShares.length,
      shares: nextShares,
      shares_by_type: byType,
    });
  };

  const removeShareLocally = (shareId) => {
    const current = getQueryData(key);
    if (!current) return;
    const nextShares = (current.shares || []).filter((s) => s.id !== shareId);
    const byType = { user: [], group: [], role: [] };
    nextShares.forEach((s) => {
      const bucket = byType[s.shared_with_type];
      if (bucket) bucket.push(s);
    });
    setQueryData(key, {
      ...current,
      total_shares: nextShares.length,
      shares: nextShares,
      shares_by_type: byType,
    });
  };

  // Cross-cache invalidations. Sharing changes the asset's visibility
  // chip and shared-with badge on the list cards — both the reports
  // list and the dashboards list have to refetch so the chips update
  // instantly. Without these, the card stays stale until reload, which
  // is the exact "share/unshare not updating" symptom users reported.
  const invalidateAssetLists = () => {
    if (assetType === 'report') invalidateQueries(['reports']);
    if (assetType === 'dashboard') invalidateQueries(['dashboards']);
  };

  const grant = useMutation({
    mutationFn: (vars) => sharesApi.grant({ assetType, assetId, ...vars }),
    onSuccess: (share) => {
      if (share?.id) overlayShare(share);
      invalidateQueries(['shares', assetType, assetId]);
      invalidateAssetLists();
    },
  });

  const update = useMutation({
    mutationFn: ({ shareId, ...patch }) => sharesApi.update(shareId, patch),
    onSuccess: (share) => {
      if (share?.id) overlayShare(share);
      invalidateQueries(['shares', assetType, assetId]);
      invalidateAssetLists();
    },
  });

  const revoke = useMutation({
    mutationFn: (shareId) => sharesApi.revoke(shareId).then(() => shareId),
    onSuccess: (shareId) => {
      removeShareLocally(shareId);
      invalidateQueries(['shares', assetType, assetId]);
      invalidateAssetLists();
    },
  });

  return { grant, update, revoke };
}
